<template>
    <div>
        <v-dialog v-model="value" scrollable persistent max-width="400px" :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-form id="editForm" ref="editForm" method="post" :action="action" @success="editSuccess">
                <input type="hidden" name="id" v-model="group.id"/>
                <v-card>
                    <v-toolbar class="v-dialog-toolbar" dark color="primary" height="80">
                        <v-text-field
                                class="headline"
                                dark
                                ma-3
                                solo-inverted
                                maxlength="255"
                                name="name"
                                ref="groupNameInput"
                                :rules="[rules.required]"
                                :error-messages="errors.name"
                                :label="$t('contact.name')"
                                append-icon="people"
                                v-model="group.name"
                        >
                        </v-text-field>
                        <v-speed-dial
                                id="contact-group-speed-dialog"
                                absolute
                                right
                                bottom
                                direction="left"
                                v-show="group.name != ''"
                        >
                            <v-btn
                                    slot="activator"
                                    color="cyan"
                                    dark
                                    fab
                            >
                                <v-icon>more_vert</v-icon>
                                <v-icon>close</v-icon>
                            </v-btn>
                            <v-tooltip bottom z-index="1000">
                                <v-btn
                                        fab
                                        dark
                                        small
                                        color="green"
                                        @click="showUploadDialog(group)"
                                        slot="activator"
                                >
                                    <v-icon>fa-upload</v-icon>
                                </v-btn>
                                <span>{{$t('action.import')}}</span>
                            </v-tooltip>
                            <v-tooltip bottom z-index="1000">
                                <v-btn
                                        fab
                                        dark
                                        small
                                        color="green"
                                        @click="showEventPicker = true"
                                        slot="activator"
                                >
                                    <v-icon>far fa-calendar-alt</v-icon>
                                </v-btn>
                                <span>{{$t('event.invite_from_event')}}</span>
                            </v-tooltip>
                            <v-tooltip bottom z-index="1000">
                                <v-btn
                                        fab
                                        dark
                                        small
                                        color="indigo"
                                        @click="showContactPicker(group)"
                                        slot="activator"
                                >
                                    <v-icon>fa-address-book</v-icon>
                                </v-btn>
                                <span>{{$t('event.invite_from_contact')}}</span>
                            </v-tooltip>
                        </v-speed-dial>
                    </v-toolbar>
                    <!--</v-card-title>-->
                    <v-divider></v-divider>
                    <v-card-text  class="v-dialog-body">
                        <v-list two-line>
                            <template v-for="(contact, index) in group.contacts">
                                <v-list-tile
                                        :key="contact.id"
                                        avatar
                                >
                                    <v-list-tile-content>
                                        <v-list-tile-title v-text="contact.name"></v-list-tile-title>
                                        <v-list-tile-sub-title>
                                            <v-icon small>phone</v-icon>{{contact.areaCode}} {{contact.phone}}
                                        </v-list-tile-sub-title>
                                    </v-list-tile-content>

                                    <v-list-tile-action>
                                        <v-icon @click="deleteContact(group.id, contact.id)">delete</v-icon>
                                    </v-list-tile-action>
                                </v-list-tile>
                                <v-divider
                                        v-if="index + 1 < group.contacts.length"
                                        :key="'divider' + contact.id"
                                ></v-divider>
                            </template>
                        </v-list>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" flat @click="$emit('input', false)">{{$t('action.close')}}</v-btn>
                        <v-btn color="blue darken-1" flat
                               type="submit"
                               ref="submit"
                               :loading="loading"
                               @click.capture="validateGroupName"
                        >
                            {{$t('action.save')}}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <event-picker
                v-model="showEventPicker"
                @success="getEvent"
        >
        </event-picker>

        <contact-picker
                v-model="contactPicker.show"
                :exclude="group.contacts"
                :eventId="contactPicker.eventId"
                :groupId="contactPicker.groupId"
                @success="getContacts"
        >
        </contact-picker>

        <upload-dialog
                ref="uploadDialog"
                v-model="uploadDialog.show"
                :title="$t('action.import')"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                :maxSizeInMB="5"
                type="event"
                bypassUpload
                @success="uploadSuccess"
        >
            <v-flex xs12 class="text-xs-right">
                <v-icon>fa-download fa-lg</v-icon> <span class="grey--text" v-html="$t('upload.use_this_template')"></span>
            </v-flex>
        </upload-dialog>
    </div>
</template>

<script>
    import RequestHandler from '~/utils/request-handler'
    import i18n from "~/i18n";
    import {UrlServices} from '~/utils/url-manager'
    import FormHandling from '~/utils/data/form-handling'
    import UploadDialog from '~/components/common/UploadDialog.vue'
    import ExcelReader from '~/utils/excel-reader'
    import importMixin from './import-mixin'
    import ContactPicker from '~/components/contact/ContactPicker'
    import EventPicker from '~/components/event/EventPicker'

    export default {
        name: "GroupEditDialog",
        components: {
            ContactPicker,
            EventPicker,
            UploadDialog
        },
        mixins: [importMixin],
        props: {
            value: Boolean,
            item: Object
        },
        data() {
            return {
                action: UrlServices.createUrl({'name': 'api_contact_group_save'}),
                group: {
                    id: 0,
                    name: '',
                    contacts: []
                },
                rules: {
                    required: value => !!value || this.$t('form.field_required')
                },
                errors: {
                    name: ''
                },
                loading: false,
                uploadDialog: {
                    show: false,
                    group: {}
                },
                contactPicker: {
                    show: false,
                    eventId: 0,
                    groupId: 0,
                    selectedContacts: [],
                    group: {}
                },
                showEventPicker: false,
                contactsDialog: {
                    show: false,
                    search: "",
                    bottomReached: false,
                    page: 1,
                    loading: false,
                    title: i18n.t('menu.contacts'),
                    group: {},
                    contacts: []
                },
            };
        },
        watch: {
            item: function() {
                this.group = _.extend({}, this.item);
            }
        },
        methods: {
            getContacts(contacts) {
                let selectedContacts = _.filter(contacts, x => {
                    return x.selected === true
                });
                let data = {
                    id: this.group.id,
                    name: this.group.id ? undefined : this.group.name,
                    contacts: _.map(selectedContacts, 'id')
                };
                let me = this;
                RequestHandler.Post(UrlServices.createUrl({'name': 'api_contact_group_save'}), data, {
                    'success': function(data){

                        me.group.id = data.id;
                        me.group.contacts = [].concat(me.group.contacts, selectedContacts);

                        me.contactPicker.show = false;

                        me.$emit('addContactsCallback', data, selectedContacts);

                        getApp.snackbar = {
                            show: true,
                            color: 'green',
                            text: me.$t('app.msg_edit_success'),
                            timeout: 3000
                        };
                    },
                    'error': function(error) {
                        console.log('error', error);
                        getApp.snackbar = {
                            show: true,
                            color: 'red',
                            text: error.msg,
                            timeout: 3000
                        };
                    }
                });
            },
            editSuccess(data) {
                this.$emit('success', data);
            },
            validateGroupName(e) {
                if (this.$refs.editForm.validate()) {
                    return true;
                }
                e.preventDefault();
                e.stopPropagation();
            },
            showContactPicker(group) {
                this.contactPicker.eventId = 0;
                this.contactPicker.groupId = 0;
                this.contactPicker.show = true;
                this.contactPicker.group = group;
            },
            showUploadDialog(group) {
                if (!this.$refs.editForm.validate()) {
                    return;
                }
                this.uploadDialog.show = true;
                this.uploadDialog.group = group;
            },
            deleteContact(groupId, contactId) {
                let me = this;
                RequestHandler.Post(UrlServices.createUrl({'name': 'api_contact_group_delete_contact'}), {group_id: groupId, contact_id: contactId}, {
                    'success': function(){
                        me.group.contacts = _.filter(me.group.contacts, x => {
                            return x.id !== contactId;
                        });

                        me.$emit('deleteContactCallback', groupId, me.group.contacts);

                        getApp.snackbar = {
                            show: true,
                            color: 'green',
                            text: me.$t('app.msg_edit_success'),
                            timeout: 3000
                        };
                    }
                });
            },
            uploadSuccess(item) {
                ExcelReader(item.file, this.importData);
            },
            importData (contacts) {
                let me = this;
                if (me.parseImportData(contacts)) {
                    let group = {
                        id: me.uploadDialog.group.id,
                        name: me.uploadDialog.group.name
                    };

                    _.each(contacts, x => {
                        x.group = group.name
                    });

                    RequestHandler.Post(UrlServices.createUrl({'name': 'api_contact_import'}), {contacts: contacts, groups: [group]}, {
                        'success': function (res) {
                            _.each(res, n => {
                                let item = _.find(me.group.contacts, x => {
                                    return x.id == n.id;
                                });
                                if (item) {
                                    item = _.extend(item, n);
                                } else {
                                    item = _.extend({}, n);
                                    me.group.contacts.push(item);
                                }
                            });

                            me.uploadDialog.show = false;
                            getApp.snackbar = {
                                show: true,
                                color: 'green',
                                text: me.$t('contact.import_success'),
                                timeout: 3000
                            };

                        }
                    });
                }
            },
            getEvent(event) {
                this.showEventPicker = false;
                this.contactPicker.groupId = 0;
                this.contactPicker.eventId = event.id;
                this.contactPicker.show = true;
            },
        },
        mounted() {
            new FormHandling(this, "editForm")
        }
    }
</script>

<style>
    .v-dialog-toolbar {
        z-index: 10;
    }
    #contact-group-speed-dialog {
        transform: translateY(70px);
    }
    #contact-group-speed-dialog > .v-btn--floating .v-btn__content {
        height: 28px;
    }
</style>